import React, { CSSProperties, useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { Tooltip } from 'components/common';
import { TEMPLATE_TAG_NAME_REGEX, TEMPLATE_TAG_SPLIT_REGEX } from 'lib/common';
import { EmailMessagePlaceholder } from 'shared-types';

interface Props {
  html: string;
  isPlainText?: boolean;
  linkColor?: string;
  paragraphSpacing?: number;
  style?: CSSProperties;
  placeholders?: { [key: string]: EmailMessagePlaceholder };
}

const RichTextValue = ({
  html,
  isPlainText = false,
  linkColor,
  paragraphSpacing = 8,
  style,
  placeholders = {}
}: Props) => {
  const { theme } = useReduxState();

  const [isMounted, setIsMounted] = useState(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const Placeholder = ({ name }) => {
    const matchingTag = placeholders[name];

    return (
      <Tooltip
        childrenWrapInDiv={false}
        content={
          matchingTag ? (
            <span>
              <span>{matchingTag.label}</span>
              {matchingTag.value ? (
                <>
                  {': '}
                  <span
                    style={{ fontWeight: 400 }}
                  >{`"${matchingTag.value}"`}</span>
                </>
              ) : null}
              {matchingTag.description ? (
                <>
                  <br />
                  <span style={{ fontSize: '1.3rem', fontWeight: 400 }}>
                    {matchingTag.description}
                  </span>
                </>
              ) : null}
            </span>
          ) : (
            <span>{'Unknown tag'}</span>
          )
        }
        contentStyle={{
          borderRadius: 7,
          fontFamily: 'Source Sans Pro, sans-serif',
          fontSize: '1.5rem',
          lineHeight: 'normal',
          padding: '8px 12px',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale'
        }}
        isLight={true}
        usePortal={true}
      >
        <span
          style={{
            backgroundColor: matchingTag
              ? theme.successLightColor
              : theme.dangerLightColor,
            borderRadius: 3,
            color: theme.textColor,
            fontWeight: 400,
            padding: 1
          }}
        >
          {`{{${name}}}`}
        </span>
      </Tooltip>
    );
  };

  const parseHtmlToReactNodes = (html: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const container = doc.body.firstChild;

    function processNode(node, key = 0) {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent
          .split(TEMPLATE_TAG_SPLIT_REGEX)
          .map((part, index) => {
            const match = part.match(TEMPLATE_TAG_NAME_REGEX);
            return match ? (
              <Placeholder key={`${key}-${index}`} name={match[1]} />
            ) : (
              part
            );
          });
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const children = Array.from(node.childNodes).map((child, i) =>
          processNode(child, i)
        );

        return React.createElement(
          node.tagName.toLowerCase(),
          { key },
          ...children
        );
      }

      return null;
    }

    return Array.from(container.childNodes).map((node, i) =>
      processNode(node, i)
    );
  };

  const parsePlainTextWithPlaceholders = (text: string) =>
    (text || '').split(TEMPLATE_TAG_SPLIT_REGEX).map((part, index) => {
      const match = part.match(TEMPLATE_TAG_NAME_REGEX);
      return match ? <Placeholder key={index} name={match[1]} /> : part;
    });

  return (
    <div className="rich-text" style={style}>
      {isMounted
        ? isPlainText
          ? parsePlainTextWithPlaceholders(html)
          : parseHtmlToReactNodes(html)
        : null}

      <style jsx>{`
        .rich-text :global(p) {
          margin: ${`${paragraphSpacing}px 0 0`};
          word-break: break-word;
        }
        .rich-text :global(p:first-child) {
          margin: 0;
        }
        .rich-text :global(a) {
          color: ${linkColor || theme.linkColor};
        }
      `}</style>
    </div>
  );
};

export default RichTextValue;
